/**
 * Renders an ICS authoring card from the form's generated section schema, so
 * the card shows the same boxes, in the same order, as the document it exports
 * to.
 *
 * The renderer takes a family and a base id rather than assuming FEMA: the
 * schema is the only thing that differs between authorities, so a Coast Guard
 * schema can be routed here later without changing this file.
 *
 * Two conventions this file depends on:
 *
 * - Dark mode is class-scoped with no global provider, so the card root carries
 *   `dark`, and any portalled surface a caller adds needs its own.
 * - Saving is per printed box, not per card and not per field. Each box owns a
 *   Save/Cancel pair that commits only its own fields; a box holding a table
 *   delegates that to its rows instead. Every commit still writes the whole
 *   record, so the document and its version stay whole.
 */

import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  useFormState,
  type DefaultValues,
  type FieldValues,
} from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { FieldGroup, FieldLegend, FieldSet } from '@/components/ui/field';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  refocusEditScope,
  useEditScopeFocus,
} from '../hooks/useEditScopeFocus';

import {
  getFormSectionSchema,
  sectionHeading,
  type FormSection,
} from './formSectionSchema';
import {
  DescriptorAutoField,
  DescriptorSectionField,
} from './DescriptorSectionField';
import {
  DescriptorSectionContext,
  useDescriptorSection,
  type DescriptorSectionApi,
} from './descriptorSectionApi';

/** React-hook-form's own value bag, so `reset` and the commits line up. */
export type DescriptorCardValues = FieldValues;

export type DescriptorCardProps = {
  /** Form base id, e.g. 'ICS_202'. */
  baseId: string;
  /** Which authority's schema is being rendered. */
  family: string;
  values: DescriptorCardValues;
  onSave: (values: DescriptorCardValues) => void;
  disabled?: boolean;
  /** Text for the `auto` boxes, keyed by source, shown read-only. */
  autoValues?: Record<string, string | null | undefined>;
  /** Replaces a generated control where an existing component owns the box. */
  slots?: Record<string, ReactNode>;
  /** Sources to leave out entirely, for boxes another card owns. */
  omit?: string[];
  /** Pratus fields the printed form has no box for. */
  extras?: ReactNode;
  /**
   * The `extras` field names, so the collapsed block gets its own Save/Cancel.
   * Without it the extras render read-write but uncommittable, so a caller
   * passing `extras` should pass these too.
   */
  extrasSources?: string[];
  extrasLabel?: string;
  /** Rendered above the sections, for version bars and the like. */
  banner?: ReactNode;
  headerActions?: ReactNode;
  className?: string;
};

/**
 * One printed box, and the fields it holds.
 *
 * Only an explicit group merges sections: the 202's box 5 asks whether a site
 * safety plan is required and where it lives, so both fields save together.
 * Ungrouped sections each stand alone, which is what gives every numbered box
 * its own Save.
 */
type SectionRun = { group: number | undefined; sections: FormSection[] };

const runsOf = (sections: FormSection[]): SectionRun[] =>
  sections.reduce<SectionRun[]>((runs, section) => {
    const last = runs[runs.length - 1];
    if (last && section.group !== undefined && last.group === section.group) {
      last.sections.push(section);
    } else {
      runs.push({ group: section.group, sections: [section] });
    }
    return runs;
  }, []);

/**
 * Save/Cancel for one printed box, shown only while that box is being worked
 * on: a card of a dozen boxes would otherwise be a wall of buttons with nothing
 * to commit.
 *
 * Enablement comes from `useFormState` scoped to this box's fields, so typing
 * in one box does not light up the buttons on every other box.
 */
const SectionActions: React.FC<{
  sources: string[];
  heading: string;
  focused: boolean;
}> = ({ sources, heading, focused }) => {
  const { commit, revert, disabled } = useDescriptorSection();
  const { control } = useFormContext();
  const { dirtyFields } = useFormState({ control, name: sources });

  const dirty = sources.some((source) =>
    Boolean((dirtyFields as Record<string, unknown>)[source])
  );
  const idle = disabled || !dirty;

  // Dirty keeps the pair on screen after focus moves away, so an edit is never
  // left with no visible way to commit or discard it.
  if (!focused && !dirty) return null;

  const settle = (act: (sources: string[]) => void) => () => {
    act(sources);
    refocusEditScope(`${sources[0]}-field`);
  };

  return (
    <div className="tw:flex tw:justify-end tw:gap-2" data-section-actions>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={idle}
        aria-label={`Cancel ${heading}`}
        onClick={settle(revert)}
      >
        Cancel
      </Button>
      <Button
        type="button"
        size="sm"
        disabled={idle}
        aria-label={`Save ${heading}`}
        onClick={settle(commit)}
      >
        Save
      </Button>
    </div>
  );
};

/**
 * One printed box: its fields, and the Save/Cancel that commits them.
 *
 * The two share a focus scope so moving from the text onto a button counts as
 * staying in the box. A run with nothing committable — an auto box, a table
 * whose rows save themselves — skips the tracking entirely rather than
 * re-rendering for a pair it will never show.
 */
const SectionBox: React.FC<{
  heading: string;
  sources: string[];
  /** Printed groups get a legend; a lone box is titled by its own field. */
  legend?: boolean;
  className?: string;
  children: ReactNode;
}> = ({ heading, sources, legend = false, className, children }) => {
  const { focused, focusProps } = useEditScopeFocus();
  const committable = sources.length > 0;

  const group = (
    <FieldGroup className={className} {...(committable ? focusProps : {})}>
      {children}
      {committable ? (
        <SectionActions sources={sources} heading={heading} focused={focused} />
      ) : null}
    </FieldGroup>
  );

  if (!legend) return group;

  return (
    <FieldSet>
      <FieldLegend variant="label">{heading}</FieldLegend>
      {group}
    </FieldSet>
  );
};

export const DescriptorCard: React.FC<DescriptorCardProps> = ({
  baseId,
  family,
  values,
  onSave,
  disabled = false,
  autoValues,
  slots,
  omit,
  extras,
  extrasSources,
  extrasLabel = 'Additional planning notes (not printed)',
  banner,
  headerActions,
  className,
}) => {
  const schema = getFormSectionSchema(baseId);

  const form = useForm<DescriptorCardValues>({
    defaultValues: values as DefaultValues<DescriptorCardValues>,
  });
  const {
    formState: { isDirty },
    getValues,
    reset,
    resetField,
  } = form;

  // Server state is the source of truth while the card is clean; once the user
  // has edits, a refetch must not overwrite them mid-sentence.
  useEffect(() => {
    if (!isDirty) reset(values, { keepDefaultValues: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  /**
   * A box's Save publishes that box and nothing else, so the payload is server
   * state with only these fields laid over it. Sending a partial bag instead
   * would blank every column the caller maps but this box does not name.
   */
  const commit = useCallback(
    (sources: string[], override?: Record<string, unknown>) => {
      const current = getValues();
      const slice: DescriptorCardValues = {};
      for (const source of sources) {
        slice[source] =
          override && source in override ? override[source] : current[source];
      }

      onSave({ ...values, ...slice });

      // Clears this box's dirty state now rather than waiting for the refetch:
      // the whole-form guard above stays blocked while any other box is dirty.
      for (const source of sources) {
        resetField(source, { defaultValue: slice[source] });
      }
    },
    [getValues, onSave, resetField, values]
  );

  const revert = useCallback(
    (sources: string[]) => {
      for (const source of sources) {
        resetField(source, { defaultValue: values[source] });
      }
    },
    [resetField, values]
  );

  const sectionApi = useMemo<DescriptorSectionApi>(
    () => ({ values, disabled, commit, revert }),
    [values, disabled, commit, revert]
  );

  const omitted = useMemo(() => new Set(omit ?? []), [omit]);
  const shown = useMemo(
    () =>
      schema?.sections.filter((section) => !omitted.has(section.source)) ?? [],
    [schema, omitted]
  );
  const runs = useMemo(() => runsOf(shown), [shown]);

  /**
   * The fields a box's own Save/Cancel commits. Auto boxes are printed from
   * incident data, slots save themselves, and a table's rows carry their own
   * actions, so a run of only those gets no buttons. That is what leaves the
   * section shells (208, 215A) with no save at all.
   */
  const committableSources = useCallback(
    (run: SectionRun): string[] =>
      run.sections
        .filter(
          (section) =>
            section.kind !== 'auto' &&
            section.kind !== 'table' &&
            !slots?.[section.source]
        )
        .map((section) => section.source),
    [slots]
  );

  if (!schema) {
    return null;
  }

  return (
    <Card
      data-testid={`descriptor-card-${baseId}`}
      data-form-family={family}
      data-ai-target="form"
      data-ai-form={baseId}
      data-ai-label={`${schema.baseId.replace('ICS_', 'ICS ')} — ${schema.title}`}
      className={cn('dark tw:bg-background tw:text-foreground', className)}
    >
      <CardHeader>
        <CardTitle>
          {schema.baseId.replace('ICS_', 'ICS ')} — {schema.title}
        </CardTitle>
        <CardDescription>
          Sections follow the {family.toUpperCase()} form this card exports to.
        </CardDescription>
        {headerActions ? <CardAction>{headerActions}</CardAction> : null}
      </CardHeader>

      <CardContent>
        {banner}
        <FormProvider {...form}>
          <DescriptorSectionContext.Provider value={sectionApi}>
            <FieldGroup>
              {runs.map((run) => {
                const group =
                  run.group !== undefined
                    ? schema.groups?.[run.group]
                    : undefined;
                const body = run.sections.map((section) =>
                  section.kind === 'auto' ? (
                    <DescriptorAutoField
                      key={section.source}
                      section={section}
                      value={autoValues?.[section.source]}
                    />
                  ) : (
                    <DescriptorSectionField
                      key={section.source}
                      section={section}
                      formBaseId={baseId}
                      disabled={disabled}
                      slot={slots?.[section.source]}
                    />
                  )
                );

                const committable = committableSources(run);
                const heading = group
                  ? sectionHeading(group)
                  : sectionHeading(run.sections[0]);

                return (
                  <SectionBox
                    key={group ? `group-${run.group}` : run.sections[0].source}
                    heading={heading}
                    sources={committable}
                    legend={!!group}
                  >
                    {body}
                  </SectionBox>
                );
              })}

              {extras ? (
                <Collapsible>
                  <Separator className="tw:mb-3" />
                  <CollapsibleTrigger
                    render={
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="tw:w-full tw:justify-start"
                      />
                    }
                  >
                    {extrasLabel}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SectionBox
                      heading={extrasLabel}
                      sources={extrasSources ?? []}
                      className="tw:pt-4"
                    >
                      {extras}
                    </SectionBox>
                  </CollapsibleContent>
                </Collapsible>
              ) : null}
            </FieldGroup>
          </DescriptorSectionContext.Provider>
        </FormProvider>
      </CardContent>
    </Card>
  );
};

export default DescriptorCard;
